import React, { useState } from 'react';
import { useClient, TIER_CONFIG } from '../contexts/ClientContext';
import { LoyaltyTier, LoyaltyReward } from '../types';
import { 
    StarIcon, 
    SparklesIcon, 
    GiftIcon, 
    TrophyIcon, 
    ZapIcon, 
    CrownIcon, 
    FlameIcon, 
    CheckCircleIcon, 
    ShieldIcon, 
    CalendarIcon,
    AwardIcon,
    LockIcon, HistoryIcon } from './Icons';

const getTierIcon = (tier: LoyaltyTier) => {
    switch (tier) {
        case 'Diamant':
            return <CrownIcon className="w-4 h-4 text-purple-300" />;
        case 'Platine':
            return <TrophyIcon className="w-4 h-4 text-cyan-300" />;
        case 'Or':
            return <SparklesIcon className="w-4 h-4 text-gold-400" />;
        case 'Argent':
            return <AwardIcon className="w-4 h-4 text-slate-300" />;
        case 'Bronze':
        default:
            return <FlameIcon className="w-4 h-4 text-amber-500" />;
    }
};

export const LoyaltyTracker: React.FC = () => {
    const { client, claimDailyBonus, redeemReward, rewardsCatalog } = useClient();
    const [rewardNotification, setRewardNotification] = useState<{ message: string; isSuccess: boolean } | null>(null);
    const [activeSubTab, setActiveSubTab] = useState<'rewards' | 'tiers' | 'history'>('rewards');

    if (!client) return null;

    const points = typeof client.loyaltyPoints === 'number' ? client.loyaltyPoints : 0;
    const paidAstres = typeof client.paidAstres === 'number' ? client.paidAstres : 0;
    const freeAstres = typeof client.freeAstres === 'number' ? client.freeAstres : 0;
    const tier: LoyaltyTier = client.loyaltyTier || 'Bronze';
    const tierInfo = TIER_CONFIG[tier] || TIER_CONFIG['Bronze'];
    const unlockedRewards = client.unlockedRewards || [];
    const transactions = client.loyaltyTransactions || [];

    // Progression vers le prochain niveau
    const nextPoints = tierInfo.nextPoints;
    let progressPercent = 100;
    let pointsNeeded = 0;

    if (nextPoints !== null) {
        const pointsInCurrentTier = points - tierInfo.minPoints;
        const totalPointsForNextTier = nextPoints - tierInfo.minPoints;
        progressPercent = Math.min(100, Math.max(0, Math.round((pointsInCurrentTier / totalPointsForNextTier) * 100)));
        pointsNeeded = Math.max(0, nextPoints - points);
    }

    const todayStr = new Date().toLocaleDateString('en-CA');
    const hasClaimedToday = client.claimedDailyBonusDate === todayStr || (typeof window !== 'undefined' && localStorage.getItem(`astro_daily_claimed_${client.id}_${todayStr}`) === 'true');

    const handleDailyClaim = () => {
        const res = claimDailyBonus();
        setRewardNotification({
            message: res.message,
            isSuccess: res.success
        });
        setTimeout(() => setRewardNotification(null), 4000);
    };

    const handleRedeem = (reward: LoyaltyReward) => {
        const res = redeemReward(reward.id);
        setRewardNotification({
            message: res.message,
            isSuccess: res.success
        });
        setTimeout(() => setRewardNotification(null), 4000);
    };

    return (
        <div className="space-y-5 animate-fade-in text-white">
            
            {/* Notification Toast */}
            {rewardNotification && (
                <div className={`p-3.5 rounded-2xl text-xs font-bold border flex items-center gap-2.5 shadow-lg transition-all animate-fade-in ${
                    rewardNotification.isSuccess 
                        ? 'bg-emerald-950/90 border-emerald-500 text-emerald-200' 
                        : 'bg-red-950/90 border-red-500 text-red-200'
                }`}>
                    {rewardNotification.isSuccess ? (
                        <CheckCircleIcon className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : (
                        <ShieldIcon className="w-4 h-4 text-red-400 shrink-0" />
                    )}
                    <span>{rewardNotification.message}</span>
                </div>
            )}

            {/* --- CARTE PRINCIPALE DU SOLDE & RANG FIDÉLITÉ --- */}
            <div className="rounded-3xl bg-gradient-to-br from-[#1E123D] via-[#140B2B] to-[#0D071E] border border-gold-500/40 p-5 sm:p-6 shadow-xl relative overflow-hidden">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1.5">
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-gold-500/20 text-gold-300 border border-gold-500/40">
                                {getTierIcon(tier)}
                                <span>Rang {tier}</span>
                            </span>
                            <span className="text-[10px] text-purple-300 font-medium">
                                • Taux : x{tierInfo.multiplier}
                            </span>
                        </div>
                        
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl sm:text-4xl font-serif font-black text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-yellow-200 to-amber-300 font-mono">
                                {points}
                            </span>
                            <span className="text-xs uppercase font-bold text-purple-300 tracking-wider">
                                Astres Disponibles
                            </span>
                        </div>
                    </div>

                    {/* Répartition Achetés vs Offerts */}
                    <div className="flex flex-wrap sm:flex-col gap-2 w-full sm:w-auto">
                        <div className="bg-black/50 border border-purple-900/50 px-3 py-1.5 rounded-xl text-xs flex items-center justify-between gap-3 flex-1 sm:flex-initial">
                            <span className="text-purple-300 text-[11px]">Achetés (à vie) :</span>
                            <strong className="text-white font-mono">{paidAstres}</strong>
                        </div>
                        <div className="bg-black/50 border border-purple-900/50 px-3 py-1.5 rounded-xl text-xs flex items-center justify-between gap-3 flex-1 sm:flex-initial">
                            <span className="text-purple-300 text-[11px]">Offerts (30j) :</span>
                            <strong className="text-amber-300 font-mono">{freeAstres}</strong>
                        </div>
                    </div>
                </div>

                {/* Barre de Progression vers le Prochain Rang */}
                {nextPoints !== null && (
                    <div className="mt-4 pt-3 border-t border-purple-900/40 space-y-1.5">
                        <div className="flex justify-between text-[10px] text-purple-300">
                            <span>Progression statut</span>
                            <span>Encore <strong>{pointsNeeded} Astres</strong> pour le palier suivant</span>
                        </div>
                        <div className="w-full h-2 bg-black/60 rounded-full overflow-hidden border border-purple-900/40">
                            <div 
                                className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-gold-400 rounded-full transition-all duration-500"
                                style={{ width: `${progressPercent}%` }}
                            ></div>
                        </div>
                    </div>
                )}
            </div>

            {/* --- CARTE BONUS QUOTIDIEN --- */}
            <div className="p-4 rounded-2xl bg-[#140C2E]/90 border border-purple-900/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-md">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gold-500/20 border border-gold-400/40 flex items-center justify-center shrink-0">
                        <GiftIcon className="w-5 h-5 text-gold-400" />
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-white">Bonus Quotidien de Fidélité</h4>
                        <p className="text-[11px] text-purple-300/80 font-light">+2 Astres offerts chaque jour de connexion</p>
                    </div>
                </div>

                <button
                    onClick={handleDailyClaim}
                    disabled={hasClaimedToday}
                    className={`w-full sm:w-auto px-5 py-2 rounded-full text-xs font-black uppercase tracking-wider transition-all cursor-pointer shadow-md shrink-0 ${
                        hasClaimedToday
                            ? 'bg-zinc-800/80 text-zinc-400 border border-zinc-700 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-400 hover:to-amber-400 text-black shadow-[0_0_15px_rgba(251,191,36,0.4)] border border-yellow-200 active:scale-95'
                    }`}
                >
                    {hasClaimedToday ? 'Déjà récupéré' : 'Réclamer (+2 Astres)'}
                </button>
            </div>

            {/* --- ONGLETS SECONDAIRES AÉRÉS --- */}
            <div className="flex items-center gap-2 border-b border-purple-900/40 pb-2">
                {[
                    { id: 'rewards', label: `Récompenses (${rewardsCatalog.length})` },
                    { id: 'tiers', label: 'Niveaux VIP' },
                    { id: 'history', label: 'Historique' }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveSubTab(tab.id as any)}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                            activeSubTab === tab.id
                                ? 'bg-purple-900/80 text-white border border-purple-400/50 shadow'
                                : 'text-purple-300 hover:text-white hover:bg-purple-950/40'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* --- CONTENU DE L'ONGLET SÉLECTIONNÉ --- */}
            
            {/* 1. RÉCOMPENSES */}
            {activeSubTab === 'rewards' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    {rewardsCatalog.map(reward => {
                        const canAfford = points >= reward.cost;
                        const isUnlocked = unlockedRewards.includes(reward.id);

                        return (
                            <div
                                key={reward.id}
                                className="p-4 rounded-2xl bg-[#0E0822] border border-purple-900/50 flex flex-col justify-between gap-3 shadow-md hover:border-purple-700/60 transition-all"
                            >
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <h4 className="text-xs font-bold text-white truncate">
                                            {reward.title}
                                        </h4>
                                        <span className="text-xs font-bold font-mono text-amber-300 bg-amber-950/60 border border-amber-500/30 px-2 py-0.5 rounded-lg shrink-0">
                                            {reward.cost} Astres
                                        </span>
                                    </div>
                                    <p className="text-[11px] text-purple-300/80 leading-relaxed font-light">
                                        {reward.description}
                                    </p>
                                </div>

                                <button
                                    onClick={() => handleRedeem(reward)}
                                    disabled={!canAfford}
                                    className={`w-full py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                        isUnlocked
                                            ? 'bg-emerald-950 border border-emerald-500/50 text-emerald-300'
                                            : canAfford
                                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white border border-purple-400/40 shadow-sm active:scale-95'
                                                : 'bg-zinc-800/60 text-zinc-500 border border-zinc-700 cursor-not-allowed'
                                    }`}
                                >
                                    {isUnlocked ? 'Débloqué' : canAfford ? `Convertir (${reward.cost} Astres)` : 'Astres insuffisants'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 2. PALIERS VIP */}
            {activeSubTab === 'tiers' && (
                <div className="space-y-2.5">
                    {Object.entries(TIER_CONFIG).map(([tName, tConf]) => {
                        const isCurrent = tier === tName;

                        return (
                            <div
                                key={tName}
                                className={`p-3.5 rounded-2xl border flex items-center justify-between gap-3 ${
                                    isCurrent
                                        ? 'bg-gradient-to-r from-purple-900/60 to-[#140C2E] border-gold-400/60 shadow-md'
                                        : 'bg-black/30 border-purple-950/60 opacity-80'
                                }`}
                            >
                                <div className="flex items-center gap-2.5">
                                    <span className="text-lg">{tConf.icon}</span>
                                    <div>
                                        <div className="flex items-center gap-1.5">
                                            <h4 className="text-xs font-bold text-white">{tName}</h4>
                                            {isCurrent && (
                                                <span className="text-[9px] bg-gold-500 text-black font-black px-1.5 py-0.2 rounded-full uppercase">
                                                    Actuel
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-purple-300/80">{tConf.description}</p>
                                    </div>
                                </div>

                                <div className="text-right shrink-0">
                                    <span className="text-xs font-mono font-bold text-amber-300 block">
                                        x{tConf.multiplier} Bonus
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                        ≥ {tConf.minPoints} Astres
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* 3. HISTORIQUE */}
            {activeSubTab === 'history' && (
                <div className="space-y-2">
                    {transactions.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">Aucune transaction enregistrée.</p>
                    ) : (
                        transactions.slice(0, 8).map(tx => (
                            <div key={tx.id} className="p-3 rounded-xl bg-black/40 border border-purple-900/40 flex justify-between items-center text-xs">
                                <div>
                                    <span className="text-white font-medium block">{tx.description}</span>
                                    <span className="text-[10px] text-gray-400">{tx.date}</span>
                                </div>
                                <span className={`font-mono font-bold ${tx.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {tx.amount > 0 ? `+${tx.amount}` : tx.amount} Astres
                                </span>
                            </div>
                        ))
                    )}
                </div>
            )}

        </div>
    );
};

export default LoyaltyTracker;
